// src/hook.ts
import * as React from "react";
import { useTheme } from "@stepstone-genesis/system";
import { useActiveMediaQuery } from "@stepstone-genesis/hooks-use-active-media-query";
import { warn as warn2, length as length2 } from "@stepstone-genesis/utilities";

// src/utils.ts
import { toArray, warn, length } from "@stepstone-genesis/utilities";
var pxToNumber = (px) => {
  return parseInt(px, 10);
};
var convertBreakpointsToNumbers = (breakpoints) => {
  return breakpoints.map(
    (breakpoint) => typeof breakpoint === "string" ? pxToNumber(breakpoint) : breakpoint
  );
};
var CONDITIONS = {
  /**
   * When the browser is `at` and only `at` a breakpoint it will display the correct content.
   * @param condition - a string that matches the breakpoint to be rendered.
   */
  at: ({ isActiveIndex, keys = [], condition }) => {
    const keyIndex = keys.indexOf(condition);
    return {
      matched: isActiveIndex === keyIndex,
      styles: keys.map((_, index) => keyIndex === index ? "block" : "none")
    };
  },
  /**
   * When the browser is `below` a breakpoint it will display the correct content.
   * @param condition - a string that matches the breakpoint to be rendered.
   */
  below: ({ isActiveIndex, keys = [], condition }) => {
    const keyIndex = keys.indexOf(condition);
    return {
      matched: isActiveIndex < keyIndex,
      styles: keys.map((_, index) => keyIndex > index ? "block" : "none")
    };
  },
  /**
   * When the browser is `below or equal to` a breakpoint it will display the correct content.
   * @param condition - a string that matches the breakpoint to be rendered.
   */
  belowOrEqualTo: ({ isActiveIndex, keys = [], condition }) => {
    const keyIndex = keys.indexOf(condition);
    return {
      matched: isActiveIndex <= keyIndex,
      styles: keys.map((_, index) => keyIndex >= index ? "block" : "none")
    };
  },
  /**
   * When the browser is `above` a breakpoint it will display the correct content.
   * @param condition - a string that matches the breakpoint to be rendered.
   */
  above: ({ isActiveIndex, keys = [], condition }) => {
    const keyIndex = keys.indexOf(condition);
    return {
      matched: isActiveIndex > keyIndex,
      styles: keys.map((_, index) => keyIndex < index ? "block" : "none")
    };
  },
  /**
   * When the browser is `above or equal to` a breakpoint it will display the correct content.
   * @param condition - a string that matches the breakpoint to be rendered.
   */
  aboveOrEqualTo: ({ isActiveIndex, keys = [], condition }) => {
    const keyIndex = keys.indexOf(condition);
    return {
      matched: isActiveIndex >= keyIndex,
      styles: keys.map((_, index) => keyIndex <= index ? "block" : "none")
    };
  },
  /**
   * When the browser is equal to any of the breakpoint supplied it will display the correct content.
   * @param condition - an array of strings that matches the breakpoint to be rendered.
   */
  when: ({ isActiveKey, keys = [], condition }) => {
    const conditionArray = toArray(condition);
    const keyIndex = conditionArray.map((item) => keys.indexOf(item));
    return {
      matched: toArray(condition).includes(isActiveKey),
      styles: keys.map((_, index) => {
        return keyIndex.includes(index) ? "block" : "none";
      })
    };
  },
  /**
   * When the browser is `between` any two breakpoints supplied it will display the correct content.
   * @param condition - an array of strings that matches the breakpoint to be rendered.
   */
  between: ({ isActiveIndex, keys = [], condition, isEqual = false }) => {
    const conditionArray = toArray(condition);
    warn({
      condition: length(conditionArray, 2),
      message: `${condition} length must be no bigger then 2`
    });
    const [from, to] = conditionArray.map((item) => keys.indexOf(item));
    const inBetween = (index = 0) => isEqual ? index >= from && index <= to : index > from && index < to;
    return {
      matched: inBetween(isActiveIndex),
      styles: keys.map((_, index) => inBetween(index) ? "block" : "none")
    };
  },
  /**
   * When the browser is `between or equal to` any two breakpoints supplied it will display the correct content.
   * @param condition - an array of strings that matches the breakpoint to be rendered.
   */
  betweenOrEqualTo: (props) => {
    return CONDITIONS["between"] ? CONDITIONS["between"]({ ...props, isEqual: true }) : { matched: false, styles: [] };
  }
};

// src/hook.ts
var useActiveBreakpoint = ({
  breakpoints,
  initialBreakpoint,
  keys,
  when,
  above,
  aboveOrEqualTo,
  below,
  belowOrEqualTo,
  at,
  between,
  betweenOrEqualTo
} = {
  initialBreakpoint: 0
}) => {
  const {
    theme: { breakpoints: themeBreakpoints = [] }
  } = useTheme();
  const bps = breakpoints != null ? breakpoints : themeBreakpoints;
  const { keys: breakpointKeys, ...activeBreakpoint } = useActiveMediaQuery({
    breakpoints: convertBreakpointsToNumbers(bps),
    initialBreakpoint: initialBreakpoint != null ? initialBreakpoint : 0,
    keys: keys != null ? keys : ["xs", "small", "medium", "large", "xl"]
  });
  const { isActiveKey } = activeBreakpoint;
  const { matched, styles } = React.useMemo(() => {
    const isActiveIndex = breakpointKeys.indexOf(isActiveKey);
    const conditions = {
      at,
      above,
      aboveOrEqualTo,
      below,
      belowOrEqualTo,
      between,
      betweenOrEqualTo,
      when
    };
    const conditionKey = Object.keys(conditions).map((key2) => conditions[key2] && key2).filter(Boolean);
    warn2({
      condition: length2(conditionKey, 2),
      message: `Please only use one of 'at', 'above', 'aboveOrEqualTo', 'below', 'belowOrEqualTo', 'between', 'betweenOrEqualTo' or 'when'. More than one is not allowed.`
    });
    const [key] = conditionKey;
    const condition = conditions[key];
    return key && condition ? CONDITIONS[key]({
      isActiveKey,
      isActiveIndex,
      keys: breakpointKeys,
      condition
    }) : { matched: false, styles: [] };
  }, [
    at,
    above,
    aboveOrEqualTo,
    below,
    belowOrEqualTo,
    between,
    betweenOrEqualTo,
    when,
    breakpointKeys,
    isActiveKey
  ]);
  return {
    ...activeBreakpoint,
    keys: breakpointKeys,
    matched,
    styles
  };
};
export {
  CONDITIONS,
  convertBreakpointsToNumbers,
  pxToNumber,
  useActiveBreakpoint
};
